import React, { useState } from "react";
import { Card, Button, Table, Modal, Alert, Spinner } from "react-bootstrap";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconEye,
  IconPrinter,
} from "@tabler/icons-react";
import LayoutAgentRank from "layouts/layout-agent-rank";
import { useAgentReward } from "./context/AgentRewardContext";
import AgentRewardForm from "./components/AgentRewardForm";
import PrintAgentReward from "./components/PrintAgentReward";
import { getContrastColor } from "utils/utils";
import { AgentProvider } from "contexts/v1/masterData/AgentContext";

function AgentRewardCRUD() {
  const { agentRewards, filterOptions, handleSearch, refetch, Pagination } =
    useAgentReward();

  const [showModal, setShowModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingReward, setViewingReward] = useState(null);
  const [printingReward, setPrintingReward] = useState(null);

  // Handle create new reward
  const handleCreate = () => {
    setEditingReward(null);
    setShowModal(true);
  };

  // Handle edit reward
  const handleEdit = (reward) => {
    setEditingReward(reward);
    setShowModal(true);
  };

  // Handle view reward details
  const handleView = (reward) => {
    setViewingReward(reward);
    setShowViewModal(true);
  };

  // Handle print reward
  const handlePrint = (reward) => {
    setPrintingReward(reward);
  };

  // Handle delete reward
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this reward?")) {
      return;
    }

    try {
      const response = await fetch(`/api/agent-reward/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      refetch(); // Refresh the data
    } catch (err) {
      console.error("Error deleting reward:", err);
      alert("Failed to delete reward. Please try again.");
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowModal(false);
    refetch(); // Refresh the data
  };

  // Handle form error
  const handleFormError = (error) => {
    console.error("Form error:", error);
    alert("Failed to save reward. Please try again.");
  };

  // Close modals
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingReward(null);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingReward(null);
  };

  return (
    <LayoutAgentRank>
      <div className="container-fluid mt-5">
        <h1>AGENT REWARDS MANAGEMENT</h1>

        <div
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
                  Agent Rewards
                </h2>
                <Button
                  variant="primary"
                  onClick={handleCreate}
                  disabled={agentRewards.loading}
                >
                  <IconPlus size={16} className="me-2" />
                  Add Reward
                </Button>
              </div>

              {/* Search */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search rewards..."
                  onKeyPress={handleSearch}
                  defaultValue={filterOptions.search}
                />
              </div>

              {agentRewards.error && (
                <Alert variant="danger" className="mb-3">
                  {agentRewards.error}
                </Alert>
              )}

              <Card>
                <div style={{ padding: "0" }}>
                  {agentRewards.loading ? (
                    <div className="text-center p-4">
                      <Spinner animation="border" />
                      <p className="mt-2">Loading rewards...</p>
                    </div>
                  ) : (
                    <Table striped bordered hover style={{ marginBottom: "0" }}>
                      <thead>
                        <tr>
                          {/* <th style={{ width: "64px" }}># ID</th> */}
                          <th>👤 Agent</th>
                          <th>🎯 Season</th>
                          <th>🎁 Reward</th>
                          <th>✅ Claim</th>
                          <th>📅 Date</th>
                          <th style={{ textAlign: "center", width: "150px" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentRewards.data.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              No rewards found. Click "Add Reward" to create
                              your first reward.
                            </td>
                          </tr>
                        ) : (
                          agentRewards.data.map((reward, index) => (
                            <tr key={reward.id || index}>
                              {/* <td style={{ fontWeight: "500" }}>{reward.id}</td> */}
                              <td>
                                <span
                                  className="badge p-2"
                                  style={{
                                    backgroundColor:
                                      reward.agent?.color || "#6c757d",
                                    color: getContrastColor(
                                      reward.agent?.color
                                    ),
                                    fontSize: "14px",
                                  }}
                                >
                                  {reward.agent?.name || "Unknown Agent"}
                                </span>
                              </td>
                              <td>{reward.season?.name || "Unknown Season"}</td>
                              <td style={{ maxWidth: "300px" }}>
                                {reward.reward}
                              </td>
                              <td>
                                {reward.is_claim ? (
                                  <span className="badge bg-success text-white fw-bold px-2 py-1">
                                    ✅ Claimed
                                  </span>
                                ) : (
                                  <span className="badge bg-danger text-white fw-bold px-2 py-1">
                                    ❌ Not Claimed
                                  </span>
                                )}
                              </td>
                              <td>
                                {new Date(reward.date).toLocaleDateString()}
                              </td>
                              <td style={{ textAlign: "center" }}>
                                <Button
                                  variant="outline-info"
                                  className="m-1"
                                  onClick={() => handleView(reward)}
                                >
                                  <IconEye size={14} />
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  className="m-1"
                                  onClick={() => handleEdit(reward)}
                                >
                                  <IconEdit size={14} />
                                </Button>
                                <Button
                                  variant="outline-success"
                                  className="m-1"
                                  onClick={() => handlePrint(reward)}
                                >
                                  <IconPrinter size={14} />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  onClick={() => handleDelete(reward.id)}
                                  className="m-1"
                                >
                                  <IconTrash size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Card>

              {/* Pagination */}
              {agentRewards.data.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  {Pagination}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create/Edit Modal */}
        <Modal className="agent-rank-scope" show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingReward ? "Edit Agent Reward" : "Add New Agent Reward"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AgentProvider>
              <AgentRewardForm
                initialData={editingReward}
                onSuccess={handleFormSuccess}
                onError={handleFormError}
              />
            </AgentProvider>
          </Modal.Body>
        </Modal>

        {/* View Modal */}
        <Modal className="agent-rank-scope" show={showViewModal} onHide={handleCloseViewModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Agent Reward Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {viewingReward && (
              <div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Agent:</strong>
                    <p>{viewingReward.agent?.name || "Unknown Agent"}</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Season:</strong>
                    <p>{viewingReward.season?.name || "Unknown Season"}</p>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Reward:</strong>
                    <p>{viewingReward.reward}</p>
                  </div>
                  <div className="col-md-6">
                    <strong>Claim Status:</strong>
                    <p>
                      {viewingReward.is_claim ? (
                        <span className="badge bg-success text-white fw-bold px-2 py-1">
                          ✅ Claimed
                        </span>
                      ) : (
                        <span className="badge bg-danger text-white fw-bold px-2 py-1">
                          ❌ Not Claimed
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <strong>Created At:</strong>
                    <p>
                      {viewingReward.created_at
                        ? new Date(viewingReward.created_at).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <strong>Updated At:</strong>
                    <p>
                      {viewingReward.updated_at
                        ? new Date(viewingReward.updated_at).toLocaleString()
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseViewModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      {/* Print Component */}
      {printingReward && (
        <PrintAgentReward
          reward={printingReward}
          refetch={refetch}
          onSuccess={() => setPrintingReward(null)}
          onError={() => setPrintingReward(null)}
        />
      )}
    </LayoutAgentRank>
  );
}

export default AgentRewardCRUD;
